<template>
  <!-- Scoreboard-style layout (like LeagueEventTile) -->
  <SectionRow
    v-if="showTeamLogos"
    row-class="league-score"
    :can-edit="showEdit && canEditEvent"
    :show-menu="showPinnedActions ? canEditEvent : showMenu"
    :hide-actions="hideActions"
    @click="emit('navigate'); router.push(`/event/${event._id}`)"
    @menu-open="emit('menuOpen')"
    @menu-close="emit('menuClose')"
  >
    <!-- Time column (desktop) -->
    <div class="league-score-time-col">
      <span class="league-score-time-col__time">{{ event.timeFrom || '' }}</span>
      <span class="league-score-time-col__date">{{ dateText }}</span>
    </div>

    <!-- Team logo -->
    <div class="league-score-team">
      <div class="league-score-logo-wrap">
        <img v-if="primaryLogoUrl" :src="primaryLogoUrl" class="league-score-logo" />
        <div v-else class="league-score-logo league-score-logo--placeholder">
          <v-icon size="20" color="rgba(var(--v-theme-sheetText), 0.2)">mdi-shield-outline</v-icon>
        </div>
      </div>
      <span class="league-score-team-name">{{ homeTeamName || event.title }}</span>
    </div>

    <!-- Center: date + time + badges -->
    <div class="league-score-center">
      <span class="league-score-league">{{ parentTeamName || event.title }}</span>
      <span class="league-score-vs">VS</span>
      <span class="league-score-date league-score-date--mobile" :class="{ 'league-score-date--small': showLeagueHeader }">{{ dateText }}</span>
      <span class="league-score-time league-score-time--mobile">{{ event.timeFrom || '' }}</span>
      <div class="match-tile-detail">
        <span class="match-tile-badge" :class="playersFull ? 'badge-warn' : 'badge-ok'">
          <span class="match-tile-badge-label">{{ $t('playersShort') }}:</span>
          {{ playersLabel }}
        </span>
        <span class="match-tile-badge" :class="goaliesFull ? 'badge-warn' : 'badge-ok'">
          <span class="match-tile-badge-label">{{ $t('goaliesShort') }}:</span>
          {{ goaliesLabel }}
        </span>
      </div>
    </div>

    <!-- Second team logo (right side) -->
    <div class="league-score-team">
      <div class="league-score-logo-wrap">
        <img v-if="primaryLogoUrl" :src="primaryLogoUrl" class="league-score-logo" />
        <div v-else class="league-score-logo league-score-logo--placeholder">
          <v-icon size="20" color="rgba(var(--v-theme-sheetText), 0.2)">mdi-shield-outline</v-icon>
        </div>
      </div>
      <span class="league-score-team-name">{{ awayTeamName || event.title }}</span>
    </div>

    <span v-if="isCanceled" class="league-score-canceled-mask"></span>
    <span v-if="isCanceled" class="league-score-canceled-stamp">{{ canceledStampLabel }}</span>

    <!-- Context menu items -->
    <template #menu="{ close }">
      <div class="context-menu-item" @click="router.push(`/event/${event._id}`); close()">
        <span>{{ $t('eventTile.detail') }}</span>
        <v-icon size="18">mdi-chevron-right</v-icon>
      </div>
      <template v-if="canEditEvent && showPinnedActions">
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="emit('unpinEvent', event); close()">
          <span>Odepnout</span>
          <v-icon size="18">mdi-pin-off-outline</v-icon>
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="emit('editEvent', event); close()">
          <span>{{ $t('eventTile.edit') }}</span>
          <v-icon size="18">mdi-pencil-outline</v-icon>
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item context-menu-item--danger" @click="emit('cancelEvent', event); close()">
          <span>Zrušit</span>
          <v-icon size="18">mdi-close-circle-outline</v-icon>
        </div>
      </template>
      <template v-else-if="showEdit && canEditEvent">
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="emit('editEvent', event); close()">
          <span>{{ $t('eventTile.edit') }}</span>
          <v-icon size="18">mdi-pencil-outline</v-icon>
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item context-menu-item--danger" @click="emit('deleteEvent', event); close()">
          <span>{{ $t('eventTile.deleteEvent') }}</span>
          <v-icon size="18">mdi-trash-can-outline</v-icon>
        </div>
      </template>
    </template>
  </SectionRow>

  <!-- Default layout (custom sections, etc.) -->
  <EventTileBase
    v-else
    :event="event"
    :showEdit="showEdit"
    :canEdit="canEditEvent"
    :showMenu="showMenu"
    :showPinnedActions="showPinnedActions"
    :showLeagueLogo="showLeagueLogo"
    :showTeamLogos="false"
    :hideActions="hideActions"
    :noAction="isAuthenticated && !isTeamMember"
    @navigate="handleNavigate"
    @unpinEvent="emit('unpinEvent', $event)"
    @deleteEvent="emit('deleteEvent', $event)"
    @cancelEvent="emit('cancelEvent', $event)"
    @editEvent="emit('editEvent', $event)"
    @menuOpen="emit('menuOpen')"
    @menuClose="emit('menuClose')"
  >
    <!-- Single team logo -->
    <template #icon>
      <img v-if="primaryLogoUrl" :src="primaryLogoUrl" alt="" class="ios-row-single-team-logo" />
      <EventIcon v-else :type="event.type" :icon="event.icon" size="22" />
    </template>

    <template #detail>
      <div class="match-tile-detail">
        <span class="match-tile-badge" :class="playersFull ? 'badge-warn' : 'badge-ok'">
          <span class="match-tile-badge-label">{{ $t('playersShort') }}:</span>
          {{ playersLabel }}
        </span>
        <span class="match-tile-badge" :class="goaliesFull ? 'badge-warn' : 'badge-ok'">
          <span class="match-tile-badge-label">{{ $t('goaliesShort') }}:</span>
          {{ goaliesLabel }}
        </span>
      </div>
    </template>

    <template #title>
      <span class="ios-row-name-text">{{ teamName || event.title }}</span>
    </template>
  </EventTileBase>
</template>

<script setup lang="ts">
import { computed, toRef, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import type { IEvent } from '@/types'
import EventTileBase from '@/components/EventTiles/EventTileBase.vue'
import SectionRow from '@/components/SectionRow.vue'
import EventIcon from '@/components/EventIcon.vue'
import { useEventPermissions } from '@/composables/useEventPermissions'
import { getTeamImage } from '@/utils/imageUtils'
import { removeTrailingNumbers } from '@/utils/teamUtils'
import { useUserStore } from '@/stores/user/userStore'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()

const props = defineProps<{
  event: IEvent
  fullDate?: boolean
  showEdit: boolean
  showMenu?: boolean
  showPinnedActions?: boolean
  showLeagueLogo?: boolean
  showTeamLogos?: boolean
  showLeagueHeader?: boolean
  hideActions?: boolean
}>()

const emit = defineEmits(['unpinEvent', 'deleteEvent', 'cancelEvent', 'editEvent', 'menuOpen', 'menuClose', 'navigate'])

const { canEditEvent } = useEventPermissions(toRef(props, 'event'), 'event-admin')

/* ── auth + team membership check ── */
const isAuthenticated = computed(() => !!userStore.user?._id)

const isTeamMember = computed(() => {
  const userTeams = userStore.user?._teams ?? []
  if (!userTeams.length) return false
  const teamRef = props.event?._team
  const teamId = typeof teamRef === 'string' ? teamRef : teamRef?._id
  return teamId ? userTeams.includes(String(teamId)) : false
})

const handleNavigate = () => {
  if (!isAuthenticated.value) {
    router.push('/login')
  } else if (isTeamMember.value) {
    router.push(`/event/${props.event._id}`)
  }
}

/* ── date formatting (for SectionRow variant) ── */
const instance = getCurrentInstance()
const $formatDate = instance?.appContext.config.globalProperties.$formatDate

const dateText = computed(() => {
  if (!props.event.dateFrom) return ''
  const from = (props.event as any).dateFromRaw ?? props.event.dateFrom
  const to = (props.event as any).dateToRaw ?? props.event.dateTo
  return $formatDate ? $formatDate(from, to) : String(from)
})

/* ── attendance badges ── */
const playersCount = computed(() => props.event.attendants?.players?.length ?? 0)
const goaliesCount = computed(() => props.event.attendants?.goalies?.length ?? 0)
const playersMax = computed(() => props.event.meta?.players ?? 0)
const goaliesMax = computed(() => props.event.meta?.goalies ?? 0)

const playersFull = computed(() => playersMax.value > 0 && playersCount.value >= playersMax.value)
const goaliesFull = computed(() => goaliesMax.value > 0 && goaliesCount.value >= goaliesMax.value)
const playersLabel = computed(() =>
  playersFull.value ? t('eventTile.reserves') : `${playersCount.value}/${playersMax.value}`
)
const goaliesLabel = computed(() =>
  goaliesFull.value ? t('eventTile.reserves') : `${goaliesCount.value}/${goaliesMax.value}`
)

/* ── team logo + name ── */
const primaryLogoUrl = computed(() => {
  const team = props.event?._team as any
  if (!team) return '/img/default-team.svg'
  const img = team.imageUrl
  return img ? getTeamImage(img) : '/img/default-team.svg'
})

const teamName = computed(() => {
  const team = props.event?._team as any
  const name = team?.name
  if (!name) return undefined
  const parts = name.split('_')
  return parts.length > 1 ? t(parts.slice(1).join('_').toLowerCase(), parts.slice(1).join('_')) : name
})

/* ── parent team name (before _) ── */
const parentTeamName = computed(() => {
  const team = props.event?._team as any
  const name = team?.name
  if (!name) return undefined
  const parts = name.split('_')
  return parts.length > 1 ? parts[0] : name
})

/* ── separate home/away names from meta ── */
const homeTeamName = computed(() => {
  const home = (props.event?.meta as any)?.homeTeam
  const name = home?.name
  if (!name) return teamName.value
  const parts = name.split('_')
  return parts.length > 1 ? t(parts.slice(1).join('_').toLowerCase(), parts.slice(1).join('_')) : removeTrailingNumbers(name)
})

const awayTeamName = computed(() => {
  const away = (props.event?.meta as any)?.awayTeam
  const name = away?.name
  if (!name) return teamName.value
  const parts = name.split('_')
  return parts.length > 1 ? t(parts.slice(1).join('_').toLowerCase(), parts.slice(1).join('_')) : removeTrailingNumbers(name)
})

const isCanceled = computed(() => Boolean((props.event as any)?.canceled))
const canceledStampLabel = computed(() => {
  const key = 'eventTile.canceledStamp'
  const label = t(key)
  return label === key ? 'CANCELED' : label
})
</script>

<style lang="scss" scoped>
/* ── Scoreboard league layout (deep for SectionRow child) ── */
:deep(.league-score) {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  gap: 10px;
  cursor: pointer;
  position: relative;
}

:deep(.league-score)::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 16px;
  right: 16px;
  height: 1px;
  background: linear-gradient(90deg,
      transparent 0%,
      rgba(var(--v-theme-sheetText), 0.12) 15%,
      rgba(var(--v-theme-sheetText), 0.12) 85%,
      transparent 100%);
}

:deep(.league-score):active {
  background: rgba(var(--v-theme-sheetText), 0.04);
}

:deep(.league-score-canceled-mask) {
  position: absolute;
  inset: 0;
  z-index: 2;
  background:
    linear-gradient(180deg, rgba(7, 10, 16, 0.16) 0%, rgba(7, 10, 16, 0.3) 100%),
    rgba(12, 16, 24, 0.18);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.04),
    inset 0 0 24px rgba(0, 0, 0, 0.14);
  backdrop-filter: saturate(0.72) blur(1.2px);
  pointer-events: none;
}

:deep(.league-score-canceled-stamp) {
  position: absolute;
  right: 36px;
  top: 10px;
  z-index: 3;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(245, 124, 0, 0.98);
  background: rgba(0, 0, 0, 0.06);
  border: 3px solid rgba(245, 124, 0, 0.95);
  border-radius: 6px;
  box-shadow:
    0 6px 14px rgba(0, 0, 0, 0.35),
    0 1px 0 rgba(255, 255, 255, 0.2) inset,
    0 0 0 1px rgba(245, 124, 0, 0.28) inset;
  text-shadow:
    0 0 1px rgba(245, 124, 0, 0.55),
    0 0 0.5px rgba(245, 124, 0, 0.75);
  padding: 5px 12px 4px;
  transform: rotate(-14deg);
  transform-origin: center;
  opacity: 0.9;
  filter: saturate(0.92) contrast(1.05);
  pointer-events: none;
  backdrop-filter: blur(0.4px);
}

/* ── Time column (desktop only) ── */
:deep(.league-score-time-col) {
  display: none;
}

/* ── VS text ── */
:deep(.league-score-vs) {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.25);
  letter-spacing: 0.08em;
  line-height: 1;
}

@media (min-width: 600px) {
  :deep(.league-score-time-col) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 52px;
    flex-shrink: 0;
    gap: 2px;
    padding-right: 8px;
    border-right: 1px solid rgba(var(--v-theme-sheetText), 0.08);
    margin-right: 4px;
  }

  :deep(.league-score-time-col__time) {
    font-size: 0.9rem;
    font-weight: 700;
    color: rgba(var(--v-theme-sheetText), 0.85);
    line-height: 1.15;
  }

  :deep(.league-score-time-col__date) {
    font-size: 0.65rem;
    font-weight: 500;
    color: rgba(var(--v-theme-sheetText), 0.4);
    white-space: nowrap;
  }

  :deep(.league-score-date--mobile) {
    display: none;
  }

  :deep(.league-score-time--mobile) {
    display: none;
  }

  :deep(.league-score-center) {
    flex: 0 0 auto;
    min-width: 24px;
  }
}

:deep(.league-score-team) {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 72px;
  flex-shrink: 0;
}

:deep(.league-score-logo-wrap) {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.league-score-logo) {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border-radius: 8px;
  flex-shrink: 0;
}

:deep(.league-score-logo--placeholder) {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-sheetText), 0.04);
}

:deep(.league-score-team-name) {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.6);
  text-align: center;
  line-height: 1.15;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

:deep(.league-score-center) {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}

:deep(.league-score-league) {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.5);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

:deep(.league-score-date) {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.7);
}

:deep(.league-score-date--small) {
  font-size: 0.7rem;
  font-weight: 400;
  color: rgba(var(--v-theme-sheetText), 0.45);
}

:deep(.league-score-time) {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.7);
}

:deep(.league-score-menu) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: rgba(var(--v-theme-sheetText), 0.4);
  border-radius: 8px;
  transition: background 0.15s ease;
}

:deep(.league-score-menu):active {
  background: rgba(var(--v-theme-sheetText), 0.08);
}

/* ── Badge styles ── */
.match-tile-detail {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.match-tile-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 7px;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.match-tile-badge-label {
  opacity: 0.85;
}

.badge-ok {
  background: #4caf50;
  color: #fff;
}

.badge-warn {
  background: #ef5350;
  color: #fff;
}

/* ── Default layout styles ── */
.ios-row-single-team-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 6px;
}

.ios-row-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
</style>
